import assetsBase from '@exodus/assets-base'
import { create } from 'superstruct'
import { BuyOrder, SellOrder } from '../schema/order.js'
import { Quote } from '../schema/quote.js'
import { createPaymentMethodValidator } from '../payment-method.js'
import { ORDER_TYPES, PROVIDERS } from '../constants.js'
import {
  DEFAULT_CRYPTO_PRECISION,
  DEFAULT_FIAT_PRECISION,
  ENABLED_ASSETS,
  ENABLED_TEST_ASSETS,
  PAYMENT_METHODS,
} from './constants.js'

const assets = assetsBase.default || assetsBase

const fromExodusToProviderAsset = (asset, assets_ = ENABLED_ASSETS) => {
  const [enabledAssets, enabledTestAssets] = [assets_, ENABLED_TEST_ASSETS].map((assetMap) =>
    Object.entries(assetMap).map((entry) => entry.reverse())
  )

  return Object.fromEntries(enabledAssets)[asset] || Object.fromEntries(enabledTestAssets)[asset]
}

const fromProviderToExodusAsset = (asset, assets_ = ENABLED_ASSETS) => assets[assets_[asset]]

const fromExodusToProviderPaymentMethod = (paymentMethod, parameters = {}) => {
  const providerPaymentMethod = [...PAYMENT_METHODS.values()].find(
    createPaymentMethodValidator({ ...parameters, type: paymentMethod })
  )

  return providerPaymentMethod?.id
}

const fromProviderToExodusPaymentMethod = (paymentMethod) => {
  if (!paymentMethod) {
    throw new Error('Payment method is required')
  }

  const type = PAYMENT_METHODS.get(paymentMethod)?.type
  return Array.isArray(type) ? type[0] : type
}

const fromProviderToExodusOrder = (
  { type = ORDER_TYPES.buy, ...order } = {},
  assets_ = ENABLED_ASSETS
) => {
  const isBuyType = type === ORDER_TYPES.buy
  const asset = fromProviderToExodusAsset(
    order[isBuyType ? 'currency' : 'baseCurrency']?.code,
    assets_
  )

  const extraFeeAmount = order.extraFeeAmount || 0
  const feeAmount = order.feeAmount || 0
  const networkFeeAmount = order.networkFeeAmount || 0
  const totalFee = feeAmount + networkFeeAmount + extraFeeAmount
  const fee = order.feeAmount || 0
  const fromAmount = order.baseCurrencyAmount + (isBuyType ? totalFee : 0)
  const toAmount = order.quoteCurrencyAmount || order.toAmountInitial || 0
  const providerPaymentMethod = type === ORDER_TYPES.buy ? order.paymentMethod : order.payoutMethod
  const depositWalletAddress =
    order?.depositWallet?.btcLegacyAddress ?? order?.depositWallet?.walletAddress

  return create(
    {
      createdAt: new Date(order.createdAt),
      extraFee: extraFeeAmount,
      fee,
      from: {
        amount: fromAmount,
        asset: isBuyType ? order.baseCurrency?.code.toUpperCase() : asset?.name,
      },
      id: order.id,
      paymentMethodType: providerPaymentMethod
        ? fromProviderToExodusPaymentMethod(providerPaymentMethod)
        : undefined,
      provider: PROVIDERS.moonpay,
      providerId: order.id,
      rate: order.usdRate,
      status: order.status,
      to: {
        amount: toAmount,
        asset: isBuyType ? asset?.name : order.quoteCurrency?.code.toUpperCase(),
      },
      totalFee,
      txId: isBuyType ? order.cryptoTransactionId : order.depositHash,
      updatedAt: new Date(order.updatedAt),
      walletAddress: isBuyType ? order.walletAddress : order.refundWalletAddress,
      ...(isBuyType
        ? {
            networkFee: order.networkFeeAmount,
            paymentMethod: order.paymentMethod,
          }
        : {
            depositWalletAddress,
          }),
    },
    type === ORDER_TYPES.buy ? BuyOrder : SellOrder
  )
}

const fromProviderToExodusQuote = ({ type = ORDER_TYPES.buy, ...quote } = {}) => {
  const isBuyType = type === ORDER_TYPES.buy
  const fromLimits = quote.baseCurrency
  const toLimits = quote.quoteCurrency || quote.currency
  const totalFee = quote.feeAmount + (quote.networkFeeAmount || 0) + (quote.extraFeeAmount || 0)
  const totalAmount = isBuyType
    ? quote.totalAmount
    : Number((quote.quoteCurrencyAmount - totalFee).toFixed(2))

  const fromPrecision = isBuyType
    ? quote.baseCurrency?.precision || DEFAULT_FIAT_PRECISION
    : quote.baseCurrency?.precision || DEFAULT_CRYPTO_PRECISION

  const toPrecision = isBuyType
    ? quote.currency?.precision || DEFAULT_CRYPTO_PRECISION
    : quote.quoteCurrency?.precision || DEFAULT_FIAT_PRECISION

  const fromAmount = quote.baseCurrencyAmount
  const toAmount = quote.quoteCurrencyAmount
  const cryptoAmount = isBuyType ? toAmount : fromAmount
  const fiatAmount = isBuyType ? fromAmount : toAmount
  const totalFeeFiat = Number.isNaN(totalFee) ? undefined : totalFee
  const totalFeeCrypto = Number.isNaN(Number(totalFeeFiat))
    ? undefined
    : (cryptoAmount * totalFeeFiat) / fiatAmount

  return create(
    {
      extraFee: quote.extraFeeAmount,
      fee: quote.feeAmount,
      from: {
        amount: fromAmount,
        asset: quote.fromAsset,
        maxBuyAmount: fromLimits?.maxBuyAmount || quote.fromMaxBuyAmount,
        maxSellAmount: fromLimits?.maxSellAmount || quote.fromMaxSellAmount,
        minBuyAmount: fromLimits?.minBuyAmount || quote.fromMinBuyAmount,
        minSellAmount: fromLimits?.minSellAmount || quote.fromMinSellAmount,
        precision: fromPrecision,
      },
      networkFee: quote.networkFeeAmount,
      paymentMethod: quote.paymentMethod,
      provider: PROVIDERS.moonpay,
      rate: quote.quoteCurrencyPrice || quote.baseCurrencyPrice,
      to: {
        amount: toAmount,
        asset: quote.toAsset,
        maxBuyAmount: toLimits?.maxBuyAmount || quote.toMaxBuyAmount,
        maxSellAmount: toLimits?.maxSellAmount || quote.toMaxSellAmount,
        minBuyAmount: toLimits?.minBuyAmount || quote.toMinBuyAmount,
        minSellAmount: toLimits?.minSellAmount || quote.toMinSellAmount,
        precision: toPrecision,
      },
      totalAmount: Number.isNaN(totalAmount) ? undefined : totalAmount,
      totalFee: totalFeeFiat,
      totalFeeCrypto,
      type,
    },
    Quote
  )
}

const moonpayAdapter = {
  fromExodusToProviderAsset,
  fromExodusToProviderPaymentMethod,
  fromProviderToExodusAsset,
  fromProviderToExodusOrder,
  fromProviderToExodusPaymentMethod,
  fromProviderToExodusQuote,
}

export default moonpayAdapter
